import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { createFileRoute } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Arc, Character } from '@/lib/types';

import { ArcFilters } from '@/components/arc/arc-filters';
import { ArcHeader } from '@/components/arc/arc-header';
import { ArcList } from '@/components/arc/arc-list';
import { QuickArcDialog } from '@/components/arc/quick-arc-dialog';
import AuthGuard from '@/components/auth-guard';
import { FeatureGuard } from '@/components/feature-guard';
import { Page } from '@/components/page';
import { useNovel } from '@/context/novel-context';
import { useEntities, useNarrativeItems } from '@/hooks/use-entities';
import { filterArcsByFilters, type ArcFilters as IArcFilters } from '@/lib/arc-utils';

function ArcContent() {
  const { novel } = useNovel();
  const { t } = useTranslation();

  // State
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [filters, setFilters] = useState<IArcFilters>({
    type: [],
    characterIds: [],
    narrativeStatus: [],
  });

  // Data Fetching
  const { data: arcs, isLoading: isLoadingArcs } = useNarrativeItems<Arc>('arcs');
  const { data: characters, isLoading: isLoadingCharacters } = useEntities<Character>('characters');

  // Filter Arcs
  const filteredArcs = useMemo(() => {
    if (!arcs) return [];
    const sorted = [...arcs].sort((a, b) => a.order - b.order);
    return filterArcsByFilters(sorted, filters);
  }, [arcs, filters]);

  const activeFilterCount =
    filters.type.length +
    filters.characterIds.length +
    filters.narrativeStatus.length;

  if (isLoadingArcs || isLoadingCharacters) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!novel.id) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6">{t('arcs.no_novel_desc')}</Typography>
      </Box>
    );
  }

  return (
    <Page
      title={t('sidebar.arcs')}
      description={t('arcs.description')}
      layoutMode="workspace"
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
        <ArcHeader
          onToggleFilters={() => setIsFilterDrawerOpen(!isFilterDrawerOpen)}
          activeFilterCount={activeFilterCount}
          onCreateArc={() => setIsCreateDialogOpen(true)}
        />

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Main List Area */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            <ArcList
              arcs={filteredArcs}
              characters={characters || []}
              novelId={novel.id}
            />
          </Box>

          {/* Right Collapsible Filters */}
          <ArcFilters
            open={isFilterDrawerOpen}
            onToggle={() => setIsFilterDrawerOpen(!isFilterDrawerOpen)}
            filters={filters}
            onFiltersChange={setFilters}
            characters={characters || []}
          />
        </Box>

        <QuickArcDialog
          open={isCreateDialogOpen}
          onClose={() => setIsCreateDialogOpen(false)}
          characters={characters || []}
        />
      </Box>
    </Page>
  );
}

export const Route = createFileRoute('/arcs/')({
  component: ArcPage,
});

export default function ArcPage() {
  return (
    <AuthGuard>
      <FeatureGuard featureKey="should_display_arcs_page">
        <ArcContent />
      </FeatureGuard>
    </AuthGuard>
  );
}
